import {
  abbreviateNumber,
  americanDateFormat,
  centsToDollars,
  dollarsToCents,
  formatDollarsFromCents,
  formatMonthYearPeriod,
  AnalyticIdentifier,
} from '@common/neo.tax';
import {
  Button,
  Checkbox,
  colors,
  CountryCell,
  PageTitle,
  ScreenLoading,
  Stack,
  Table,
  TableContainer,
  Tooltip,
  Typography,
  useModal,
} from '@ui-components/neo.tax';
import uniqueId from 'lodash/uniqueId';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { CellProps, Column } from 'react-table';

import { useAscContext } from '@neo.tax/asc/context';
import { useAscPageLockStatus, useAscWages, type AscWage } from '@neo.tax/asc/hooks';
import { useAscWageSummary, type AscWageSummary } from '@neo.tax/asc/hooks/useAscWageSummary';
import {
  TABLE_CELL_PLACEHOLDER,
  ColumnDefinition,
  FilterPopover,
  FilterSortDataType,
  formatContributorName,
  getCountryCodeAlpha2,
  KeywordSearch,
  PageLayout,
  AscWageSortFields,
  SortDirection,
  SortPopover,
  useColumnVisibility,
  useFilterConditions,
  useKeywordSearch,
  useCurrentUserScopeForCurrentCompany,
  useSorting,
  ColumnVisibilityPopover,
  useCurrentCompanyId,
  PercentCell,
  BulkAllocationModal,
  TableHeader,
} from '@neo.tax/shared';

import { AscCostUploadModal } from '../asc-cost-upload-modal';
import { BulkEditAscWageModal } from '../bulk-edit-asc-wage-modal';
import { CombineContributorModal } from '../combine-contributor-modal';
import { EditContributorAllocationModalLoader } from '../contributor-allocation-modal';
import { FooterCellStyled, TypographyFullWidth, customCellClassNames, StyledButton } from './asc-wages-layout.styled';
import { AscWageNameCell } from './AscWageNameCell';
import { useAscWagesBulkEditAnalyticIds, useApplyWeightedCapitalizedPercentageToContributors } from './hooks';

const ascWagesTableColumns = ({
  ascWages,
  footerData,
  limitAccessToFinancialData,
  onSelectAllRows,
  onSelectRow,
  selectedRowIds,
  setModalContributorId,
  visibleColumns,
}: {
  footerData: AscWageSummary | null;
  limitAccessToFinancialData: boolean;
  setModalContributorId: (id: string) => void;
  visibleColumns: AscWageSortFields[];
  selectedRowIds: string[];
  onSelectAllRows: (checked: boolean) => void;
  onSelectRow: (checked: boolean, item: AscWage) => void;
  ascWages: AscWage[] | null;
}): Column<AscWage>[] => {
  const amountCentsColumns: Column<AscWage>[] = limitAccessToFinancialData
    ? []
    : [
        {
          id: AscWageSortFields.CapitalizedTotalCompensationCents,
          accessor: 'capitalizedTotalCompensationCents',
          width: 160,
          Header: () => <TableHeader textAlign="right">Capitalized $</TableHeader>,
          Cell: ({ value }: CellProps<AscWage, AscWage['capitalizedTotalCompensationCents']>): ReactElement => (
            <TypographyFullWidth as="p" color="inherit" fontSize="inherit" textAlign="right">
              {value != null ? formatDollarsFromCents(value, { showCents: false }) : TABLE_CELL_PLACEHOLDER}
            </TypographyFullWidth>
          ),
          Footer: (): ReactElement => {
            const hasData = (footerData?.sum?.capitalizedTotalCompensationCents ?? null) !== null;
            const abbreviatedValue = abbreviateNumber({
              inputNumber: centsToDollars(footerData?.sum?.capitalizedTotalCompensationCents ?? 0),
            }).abbreviation;
            const value = hasData ? `$${abbreviatedValue}` : '0';
            return <FooterCellStyled preText="TOTAL" text={value} alignText="end" />;
          },
        },
        {
          id: AscWageSortFields.TotalCompensationCents,
          accessor: 'totalCompensationCents',
          width: 140,
          Header: () => <TableHeader textAlign="right">Total Amount $</TableHeader>,
          Cell: ({ value }: CellProps<AscWage, AscWage['totalCompensationCents']>): ReactElement => (
            <TypographyFullWidth as="p" color="inherit" fontSize="inherit" textAlign="right">
              {value != null ? formatDollarsFromCents(value, { showCents: false }) : TABLE_CELL_PLACEHOLDER}
            </TypographyFullWidth>
          ),
          Footer: (): ReactElement => {
            const hasData = (footerData?.sum?.totalCompensationCents ?? null) !== null;
            const abbreviatedValue = abbreviateNumber({
              inputNumber: centsToDollars(footerData?.sum?.totalCompensationCents ?? 0),
            }).abbreviation;
            const value = hasData ? `$${abbreviatedValue}` : '0';
            return <FooterCellStyled preText="TOTAL" text={value} alignText="end" />;
          },
        },
        {
          id: AscWageSortFields.SalaryCompensationCents,
          accessor: 'salaryCompensationCents',
          width: 140,
          Header: () => <TableHeader textAlign="right">Salary Cost $</TableHeader>,
          Cell: ({ value }: CellProps<AscWage, AscWage['salaryCompensationCents']>): ReactElement => (
            <TypographyFullWidth as="p" color="inherit" fontSize="inherit" textAlign="right">
              {value != null ? formatDollarsFromCents(value, { showCents: false }) : TABLE_CELL_PLACEHOLDER}
            </TypographyFullWidth>
          ),
          Footer: (): ReactElement => {
            const hasData = (footerData?.sum?.salaryCompensationCents ?? null) !== null;
            const abbreviatedValue = abbreviateNumber({
              inputNumber: centsToDollars(footerData?.sum?.salaryCompensationCents ?? 0),
            }).abbreviation;
            const value = hasData ? `$${abbreviatedValue}` : '0';
            return <FooterCellStyled preText="TOTAL" text={value} alignText="end" />;
          },
        },
        {
          id: AscWageSortFields.StockCompensationCents,
          accessor: 'stockCompensationCents',
          width: 140,
          Header: () => <TableHeader textAlign="right">SBC Cost $</TableHeader>,
          Cell: ({ value }: CellProps<AscWage, AscWage['stockCompensationCents']>): ReactElement => (
            <TypographyFullWidth as="p" color="inherit" fontSize="inherit" textAlign="right">
              {value != null ? formatDollarsFromCents(value, { showCents: false }) : TABLE_CELL_PLACEHOLDER}
            </TypographyFullWidth>
          ),
          Footer: (): ReactElement => {
            const hasData = (footerData?.sum?.stockCompensationCents ?? null) !== null;
            const abbreviatedValue = abbreviateNumber({
              inputNumber: centsToDollars(footerData?.sum?.stockCompensationCents ?? 0),
            }).abbreviation;
            const value = hasData ? `$${abbreviatedValue}` : '0';
            return <FooterCellStyled preText="TOTAL" text={value} alignText="end" />;
          },
        },
      ];

  const columns: Column<AscWage>[] = [
    {
      id: AscWageSortFields.Name,
      accessor: 'name',
      width: 'minmax(300px, 400px)',
      Header: () => (
        <Stack direction="row" alignItems="center" gap={2}>
          <Checkbox
            id="select-all"
            name="select-all"
            checked={selectedRowIds.length === (ascWages?.length || 0) && (ascWages?.length || 0) > 0}
            onChange={(checked: boolean) => onSelectAllRows(checked)}
          />
          <TableHeader>Name</TableHeader>
        </Stack>
      ),
      Cell: (cell: CellProps<AscWage, AscWage['name']>): ReactElement => (
        <Stack direction="row" alignItems="center" gap={2}>
          <Checkbox
            id={`select-${cell.row.original.id}`}
            name={`select-${cell.row.original.id}`}
            checked={selectedRowIds.some((selectedRow) => selectedRow === cell.row.original.id)}
            onChange={(checked: boolean) => onSelectRow(checked, cell.row.original)}
          />
          <AscWageNameCell
            name={formatContributorName(cell.row.original.name)}
            hasCompleteDataInPeriod={cell.row.original.hasCompleteDataInPeriod}
          />
        </Stack>
      ),
      Footer: (): ReactElement => {
        const value = footerData?.count?.name ?? '0';
        return <FooterCellStyled preText="COUNT" text={value} />;
      },
    },
    {
      id: AscWageSortFields.Titles,
      accessor: 'titles',
      width: 'minmax(170px, 260px)',
      Header: () => <TableHeader>Title</TableHeader>,
      Cell: ({ value }: CellProps<AscWage, AscWage['titles']>): ReactElement => (
        <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
          {value || TABLE_CELL_PLACEHOLDER}
        </Typography>
      ),
    },
    {
      id: AscWageSortFields.Departments,
      accessor: 'departments',
      width: 170,
      Header: () => <TableHeader>Department</TableHeader>,
      Cell: ({ value }: CellProps<AscWage, AscWage['departments']>): ReactElement => (
        <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
          {value || TABLE_CELL_PLACEHOLDER}
        </Typography>
      ),
    },
    {
      id: AscWageSortFields.ManagerNames,
      accessor: 'managerNames',
      width: 190,
      Header: () => <TableHeader>Manager</TableHeader>,
      Cell: ({ value }: CellProps<AscWage, AscWage['managerNames']>): ReactElement => (
        <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
          {value || TABLE_CELL_PLACEHOLDER}
        </Typography>
      ),
    },
    {
      id: AscWageSortFields.HireDates,
      accessor: 'hireDates',
      width: 120,
      Header: () => <TableHeader>Hire Date</TableHeader>,
      Cell: ({ value }: CellProps<AscWage, AscWage['hireDates']>): ReactElement => (
        <Typography color="inherit" fontSize="inherit">
          {value
            ? value
                .split(', ')
                .map((dateString) => americanDateFormat(dateString, { zeroPadded: false }))
                .join(', ')
            : TABLE_CELL_PLACEHOLDER}
        </Typography>
      ),
    },
    {
      id: AscWageSortFields.TerminationDates,
      accessor: 'terminationDates',
      width: 120,
      Header: () => <TableHeader>Term. Date</TableHeader>,
      Cell: ({ value }: CellProps<AscWage, AscWage['terminationDates']>): ReactElement => (
        <Typography color="inherit" fontSize="inherit">
          {value
            ? value
                .split(', ')
                .map((dateString) => americanDateFormat(dateString, { zeroPadded: false }))
                .join(', ')
            : TABLE_CELL_PLACEHOLDER}
        </Typography>
      ),
    },
    {
      id: AscWageSortFields.CostCenters,
      accessor: 'costCenters',
      width: 190,
      Header: () => <TableHeader>Cost Center</TableHeader>,
      Cell: ({ value }: CellProps<AscWage, AscWage['costCenters']>): ReactElement => (
        <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
          {value || TABLE_CELL_PLACEHOLDER}
        </Typography>
      ),
    },
    {
      id: AscWageSortFields.Entities,
      accessor: 'entities',
      width: 190,
      Header: () => <TableHeader>Entity</TableHeader>,
      Cell: ({ value }: CellProps<AscWage, AscWage['entities']>): ReactElement => (
        <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
          {value || TABLE_CELL_PLACEHOLDER}
        </Typography>
      ),
    },
    {
      id: AscWageSortFields.AllocationPercent,
      accessor: 'allocationPercent',
      width: 140,
      Header: () => <TableHeader textAlign="right">Capitalized %</TableHeader>,
      Cell: ({ row, value }: CellProps<AscWage, AscWage['allocationPercent']>): ReactElement => (
        <PercentCell
          onClick={(): void => setModalContributorId(row.original.contributorId)}
          percent={value}
          isSelected={row.isSelected}
        />
      ),
      Footer: (): ReactElement => {
        const hasData = (footerData?.avg?.allocationPercent ?? null) !== null;
        const value = hasData ? `${Math.round(footerData?.avg?.allocationPercent ?? 0)}%` : '0';
        return <FooterCellStyled preText="AVG" text={value} alignText="end" />;
      },
    },
    ...amountCentsColumns,
    {
      id: AscWageSortFields.Countries,
      accessor: (datum: AscWage): string | null | undefined => datum.countries,
      width: 110,
      Header: () => <TableHeader>Country</TableHeader>,
      Cell: ({ value }: CellProps<AscWage, string | null | undefined>): ReactElement | string =>
        typeof value === 'string' && value.length ? (
          <CountryCell countryCode={getCountryCodeAlpha2(value)}>{value}</CountryCell>
        ) : (
          TABLE_CELL_PLACEHOLDER
        ),
    },
  ];

  return columns.filter((column) => visibleColumns.includes(column.id as AscWageSortFields));
};

const sortAndFilterableColumnsForLimitedAccessUser = [
  { name: AscWageSortFields.Name, label: 'Name', dataType: FilterSortDataType.TEXT },
  { name: AscWageSortFields.Titles, label: 'Title', dataType: FilterSortDataType.TEXT },
  { name: AscWageSortFields.Departments, label: 'Department', dataType: FilterSortDataType.TEXT },
  { name: AscWageSortFields.ManagerNames, label: 'Manager', dataType: FilterSortDataType.TEXT },
  { name: AscWageSortFields.HireDates, label: 'Hire Date', dataType: FilterSortDataType.DATE },
  { name: AscWageSortFields.TerminationDates, label: 'Termination Date', dataType: FilterSortDataType.DATE },
  { name: AscWageSortFields.CostCenters, label: 'Cost Center', dataType: FilterSortDataType.TEXT },
  { name: AscWageSortFields.Entities, label: 'Entity', dataType: FilterSortDataType.TEXT },
  { name: AscWageSortFields.AllocationPercent, label: 'Capitalized %', dataType: FilterSortDataType.NUMBER },
  { name: AscWageSortFields.Countries, label: 'Country', dataType: FilterSortDataType.TEXT },
] as const satisfies readonly ColumnDefinition<AscWageSortFields>[];

const indexOfAllocationPercentColumn = sortAndFilterableColumnsForLimitedAccessUser.findIndex(
  (column) => column.name === AscWageSortFields.AllocationPercent,
);
const sortAndFilterableColumnsForFullAccessUser = [
  ...sortAndFilterableColumnsForLimitedAccessUser.slice(0, indexOfAllocationPercentColumn + 1),
  {
    name: AscWageSortFields.CapitalizedTotalCompensationCents,
    label: 'Capitalized $',
    dataType: FilterSortDataType.NUMBER,
    toHuman: (n): number | null => (n === null ? null : centsToDollars(Number(n))),
    fromHuman: (n): number | null => (n === null ? null : dollarsToCents(Number(n))),
  },
  {
    name: AscWageSortFields.TotalCompensationCents,
    label: 'Total Amount $',
    dataType: FilterSortDataType.NUMBER,
    toHuman: (n): number | null => (n === null ? null : centsToDollars(Number(n))),
    fromHuman: (n): number | null => (n === null ? null : dollarsToCents(Number(n))),
  },
  {
    name: AscWageSortFields.SalaryCompensationCents,
    label: 'Salary Cost $',
    dataType: FilterSortDataType.NUMBER,
    toHuman: (n): number | null => (n === null ? null : centsToDollars(Number(n))),
    fromHuman: (n): number | null => (n === null ? null : dollarsToCents(Number(n))),
  },
  {
    name: AscWageSortFields.StockCompensationCents,
    label: 'SBC Cost $',
    dataType: FilterSortDataType.NUMBER,
    toHuman: (n): number | null => (n === null ? null : centsToDollars(Number(n))),
    fromHuman: (n): number | null => (n === null ? null : dollarsToCents(Number(n))),
  },
  ...sortAndFilterableColumnsForLimitedAccessUser.slice(indexOfAllocationPercentColumn + 1),
] as const satisfies readonly ColumnDefinition<AscWageSortFields>[];

export const AscWagesLayout = (): ReactElement => {
  const [modalContributorId, setModalContributorId] = useState<string | undefined>(undefined);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [combineContributorInput, setCombineContributorInput] = useState<
    { sourceContributorId: string; targetContributorId: string } | undefined
  >(undefined);
  const [isOpenAscCostUploadModal, setIsOpenAscCostUploadModal] = useState<boolean>(false);
  const { fiscalYearId, period, periodName } = useAscContext();
  const companyId = useCurrentCompanyId();
  const { end, start } = formatMonthYearPeriod(period);
  const { limitAccessToFinancialData } = useCurrentUserScopeForCurrentCompany();
  const { isOpen: isBulkEditModalOpen, setIsOpen: setIsBulkEditModalOpen } = useModal();
  const { isOpen: isBulkAllocationModalOpen, setIsOpen: setIsBulkAllocationModalOpen } = useModal();
  const ascWagesBulkEditAnalyticIds = useAscWagesBulkEditAnalyticIds();
  const { loading: loadingPageLockStatus, pageLockStatus } = useAscPageLockStatus({
    disableIfOngoingSync: true,
    disableIfAscCostPendingValidation: true,
    disableIfPeriodLocked: true,
  });

  const searchKeywordState = useKeywordSearch();

  const sortAndFilterableColumns = limitAccessToFinancialData
    ? sortAndFilterableColumnsForLimitedAccessUser
    : sortAndFilterableColumnsForFullAccessUser;
  const initialSorting = [
    {
      id: uniqueId('AscWageSortField'),
      column: AscWageSortFields.AllocationPercent,
      direction: SortDirection.Desc,
    },
  ];
  if (!limitAccessToFinancialData) {
    initialSorting.push({
      id: uniqueId('AscWageSortField'),
      column: AscWageSortFields.TotalCompensationCents,
      direction: SortDirection.Desc,
    });
  }

  const filterState = useFilterConditions({ columns: sortAndFilterableColumns, persistenceKey: 'ascWages' });
  const sortState = useSorting({
    columns: sortAndFilterableColumns,
    initialSorting,
    persistenceKey: 'ascWages',
  });
  const columnVisibilityState = useColumnVisibility({
    columns: sortAndFilterableColumns,
    alwaysVisibleColumns: [AscWageSortFields.Name, AscWageSortFields.AllocationPercent],
    initialVisibleColumns: [
      AscWageSortFields.Departments,
      AscWageSortFields.Titles,
      AscWageSortFields.ManagerNames,
      AscWageSortFields.Countries,
      AscWageSortFields.CapitalizedTotalCompensationCents,
      AscWageSortFields.TotalCompensationCents,
    ],
    persistenceKey: 'ascWages',
  });

  // memo inputs
  const sorting = useMemo(
    () => sortState.sorts.map((s) => ({ field: s.column, direction: s.direction })),
    [sortState.sorts],
  );

  const { applyAllocation, isAllocating } = useApplyWeightedCapitalizedPercentageToContributors({
    filterConditions: filterState.conditions,
    searchKeyword: searchKeywordState.searchKeyword,
    sorting,
  });

  const { ascWages, isLoadingMore, loading, onLoadMoreItems } = useAscWages({
    searchKeyword: searchKeywordState.searchKeyword,
    filterConditions: filterState.conditions,
    sorting,
  });
  const { ascWageSummary, loading: loadingSummary } = useAscWageSummary({
    filterConditions: filterState.conditions,
    searchKeyword: searchKeywordState.searchKeyword,
  });

  // Selection handlers. Note this will only select rows that are visible in the table.
  // Selection does not apply to rows that have not been loaded.
  const handleSelectAllRows = useCallback(
    (checked: boolean): void => {
      if (checked) {
        setSelectedRowIds(ascWages?.map((wage) => wage.id) || []);
      } else {
        setSelectedRowIds([]);
      }
    },
    [ascWages],
  );

  const handleSelectRow = useCallback((checked: boolean, item: AscWage): void => {
    if (checked) {
      setSelectedRowIds((prev) => [...prev, item.id]);
    } else {
      setSelectedRowIds((prev) => prev.filter((row) => row !== item.id));
    }
  }, []);

  const handleCloseCombineModal = useCallback((): void => {
    setCombineContributorInput(undefined);
    setSelectedRowIds([]);
  }, []);

  // Merge button handler - requires exactly 2 rows selected
  const handleMergeClick = useCallback((): void => {
    if (selectedRowIds.length !== 2 || !ascWages) {
      return;
    }

    const selectedWages = ascWages.filter((wage) => selectedRowIds.includes(wage.id));
    if (selectedWages.length !== 2) {
      return;
    }

    // Sort by name ASC to determine target (first) and source (second)
    // This ensures consistent behavior regardless of selection order
    const sortedWages = [...selectedWages].sort((a, b) => {
      const nameA = a.name ?? '';
      const nameB = b.name ?? '';
      return nameA.localeCompare(nameB);
    });

    const [targetWage, sourceWage] = sortedWages;

    setCombineContributorInput({
      sourceContributorId: sourceWage.contributorId,
      targetContributorId: targetWage.contributorId,
    });
  }, [selectedRowIds, ascWages]);

  // Bulk allocation submit handler
  const handleBulkAllocationSubmit = async (allocationPercent: number): Promise<void> => {
    const selectedWages = ascWages?.filter((wage) => selectedRowIds.includes(wage.id)) || [];
    const success = await applyAllocation(selectedWages, allocationPercent);

    if (success) {
      // Clear selection and close modal
      setSelectedRowIds([]);
      setIsBulkAllocationModalOpen(false);
      // TODO: Refresh data or show success message
    }
  };

  const columns = useMemo(
    () =>
      ascWagesTableColumns({
        footerData: ascWageSummary,
        limitAccessToFinancialData,
        setModalContributorId,
        visibleColumns: columnVisibilityState.visibleColumns,
        selectedRowIds,
        onSelectAllRows: handleSelectAllRows,
        onSelectRow: handleSelectRow,
        ascWages,
      }),
    [
      ascWageSummary,
      columnVisibilityState,
      limitAccessToFinancialData,
      selectedRowIds,
      handleSelectAllRows,
      handleSelectRow,
      ascWages,
    ],
  );

  // Nothing behind this page turns a write away, so it cannot be handed over before the lock status
  // is known: rendering it now and fading it a moment later leaves a window in which a closed period
  // looks editable and the writes made in it land.
  if (loadingPageLockStatus) {
    return <ScreenLoading />;
  }

  return (
    <>
      <PageLayout
        title={
          <PageTitle>
            ASC 350-40 Cost
            <>
              {' | '}
              <Typography color={colors.blue[600]} fontSize="inherit" fontWeight="inherit">
                {start} - {end}
              </Typography>
            </>
          </PageTitle>
        }
        subtitle="Review and modify details for ASC 350-40 Cost"
        pageHeaderBorder
        trailingContent={
          <>
            <Button
              size="sm"
              variant="primary"
              rounded="regular"
              id={ascWagesBulkEditAnalyticIds.ascWagesEditInBulkBtnId}
              disabled={pageLockStatus.locked}
              onClick={(): void => setIsBulkEditModalOpen(true)}
            >
              Edit in Excel
            </Button>
            {limitAccessToFinancialData ? null : (
              // PageLayout fades a locked page with opacity and pointer-events, which stops a mouse
              // but leaves the button in the tab order — and the modal it opens is a portal, so it
              // renders outside the faded subtree and works normally. Disable it properly.
              <Button
                size="sm"
                variant="primary"
                rounded="regular"
                disabled={pageLockStatus.locked}
                onClick={(): void => {
                  setIsOpenAscCostUploadModal(true);
                }}
              >
                Upload ASC 350-40 Cost
              </Button>
            )}
          </>
        }
        locked={pageLockStatus.locked}
        lockedReason={pageLockStatus.lockedReason}
        lockButtons={pageLockStatus.lockButtons}
        fullWidth
      >
        <Stack gap={5}>
          <KeywordSearch {...searchKeywordState} />
          <FilterPopover itemsDescription="expenses" {...filterState} />
          <SortPopover itemsDescription="expenses" {...sortState} />
          <ColumnVisibilityPopover {...columnVisibilityState} />
          <Tooltip
            text={
              // TODO: This currently only covers period selection. We will need to add a check for YTD.
              !periodName.startsWith('M')
                ? 'Set Cap % is only available when a single month is selected'
                : selectedRowIds.length === 0
                  ? 'Select at least one contributor to set the capitalized %'
                  : undefined
            }
          >
            <StyledButton
              size="base"
              variant="primary"
              rounded="regular"
              disabled={selectedRowIds.length === 0 || !periodName.startsWith('M')}
              onClick={(): void => setIsBulkAllocationModalOpen(true)}
            >
              Set Cap %
            </StyledButton>
          </Tooltip>
          <StyledButton
            size="base"
            variant="primary"
            rounded="regular"
            disabled={selectedRowIds.length !== 2}
            onClick={handleMergeClick}
          >
            Merge
          </StyledButton>
        </Stack>
        <Stack fullHeight>
          {loading && !ascWages ? (
            <ScreenLoading />
          ) : (
            <TableContainer height="100%">
              <Table
                columns={columns}
                data={ascWages || []}
                customCellClassNames={[customCellClassNames]}
                endReached={onLoadMoreItems}
                isFooterLoading={loadingSummary || isLoadingMore}
                isBodyLoading={loading}
              />
            </TableContainer>
          )}
          {/* https://linear.app/neo-tax/issue/NEO-5053/update-asc-cost-mutation */}
          {/* <>Add Detail panel view</> */}
        </Stack>
        <BulkEditAscWageModal
          ascWagesBulkEditAnalyticIds={ascWagesBulkEditAnalyticIds}
          isOpen={isBulkEditModalOpen}
          onClose={(): void => setIsBulkEditModalOpen(false)}
        />

        <BulkAllocationModal
          loading={isAllocating}
          setIsOpen={setIsBulkAllocationModalOpen}
          submit={handleBulkAllocationSubmit}
          headerText={`Set Capitalized % for ${selectedRowIds.length} ${
            selectedRowIds.length === 1 ? 'Expense' : 'Expenses'
          }`}
          cancelButtonId={
            AnalyticIdentifier.ASC_LAYOUT_APPLY_WEIGHTED_CAPITALIZED_PERCENTAGE_TO_CONTRIBUTORS_CANCEL_BUTTON
          }
          sliderLabelText="Capitalized %"
          submitButtonId={AnalyticIdentifier.ASC_LAYOUT_APPLY_WEIGHTED_CAPITALIZED_PERCENTAGE_TO_CONTRIBUTORS_BUTTON}
          isOpen={isBulkAllocationModalOpen}
          copy={
            <Stack paddingY={6} direction="column">
              <Typography as="p" fontSize="sm" color={colors.gray[500]} textAlign="left">
                Enter the target Capitalized % percentage. This will replace existing project allocations for the
                selected expenses. The target Capitalized percent will be distributed proportionally across capitalized
                projects based on their current share of capitalized time across all employees.
              </Typography>
            </Stack>
          }
        />
        <AscCostUploadModal
          close={(): void => {
            setIsOpenAscCostUploadModal(false);
          }}
          companyId={companyId}
          fiscalYearId={fiscalYearId}
          isOpen={isOpenAscCostUploadModal}
        />
      </PageLayout>
      {modalContributorId && (
        <EditContributorAllocationModalLoader
          isOpen={!!modalContributorId}
          close={(): void => setModalContributorId(undefined)}
          contributorId={modalContributorId}
        />
      )}
      <CombineContributorModal
        isOpen={!!combineContributorInput}
        onClose={handleCloseCombineModal}
        combineContributorInput={combineContributorInput}
      />
    </>
  );
};
